import type { WizardStep } from '@/components/wizards';
import { SeedTypeOption } from '@/types';

import type { SeedWizardFormData } from './AddSeedByTypeModal.types';

/**
 * Step ID constants for switch statements.
 * Using constants prevents typo bugs and enables IDE autocomplete.
 */
export const STEP_IDS = {
  SELECT_TYPE: 'select-type',
  ADD_SEEDS: 'add-seeds',
  CONFIGURE_AUTH: 'configure-auth',
} as const;

/**
 * Wizard step configuration for Add Seed modal.
 * Three-step wizard for web apps: type selection -> data entry -> auth config.
 * Non-web-app types skip step 3 via shouldSkip.
 */
export const seedWizardSteps: WizardStep<SeedWizardFormData>[] = [
  {
    id: STEP_IDS.SELECT_TYPE,
    order: 1,
    title: 'Select Type',
    description: 'Choose the type of seed to add',
    validate: data => data.seedType !== null,
  },
  {
    id: STEP_IDS.ADD_SEEDS,
    order: 2,
    title: 'Add Seeds',
    description: 'Enter seed data or upload files',
    // NOTE: Cannot fully validate here - file state is external.
    // Full validation handled by canSubmit in modal (Hybrid State Pattern).
    validate: () => true,
  },
  {
    id: STEP_IDS.CONFIGURE_AUTH,
    order: 3,
    title: 'Authentication',
    description: 'Configure web authentication headers',
    isFinal: true,
    shouldSkip: data =>
      data.seedType !== SeedTypeOption.WEB_APPLICATION &&
      data.seedType !== SeedTypeOption.WEB_SERVICE,
    validate: () => true, // Auth is optional
  },
];

/**
 * Initial form data for wizard.
 * Reset to these values when modal opens or wizard resets.
 */
export const DEFAULT_SEED_FORM_DATA: SeedWizardFormData = {
  seedType: null,
  textInput: '',
  authMode: 'skip',
  authLabel: '',
  authHeaders: [{ key: '', value: '' }],
};
